import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { List, Button, Typography, Badge, Input, Switch, Popconfirm, Tooltip } from 'antd';
import { listConversations } from '../../infrastructure/apiClient';
import { deleteThread } from '../../apis/ThreadService';
import { useClients } from '../../context/ClientContext';
import dayjs from 'dayjs';

interface Props {
  visible: boolean;
  onToggle: () => void;
  onSelectConversation: (id: string) => void;
}

const ConversationHistoryPanel = ({ visible, onToggle, onSelectConversation }: Props) => {
  const [conversations, setConversations] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState({ offset: 0, limit: 20 });
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const { activeClient } = useClients();

  // Debounce search query
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setDebouncedQuery(query.trim()), 450);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [query]);

  const loadPage = useCallback(async (reset = false) => {
    if (loading || !activeClient) return;
    setLoading(true);
    try {
      const targetOffset = reset ? 0 : pageState.offset;
      const { conversations: page, total: t, offset, limit } = await listConversations(
        { 
          tenantId: activeClient.id,
          offset: targetOffset,
          limit: pageState.limit,
          q: debouncedQuery,
          includeArchived
        }
      );
      const mapped = page.map(c => ({ ...c, createdAt: new Date(c.createdAt), messages: c.messages || [] }));
      setTotal(t);
      setConversations(prev => (reset ? mapped : [...prev, ...mapped.filter(n => !prev.some(p => p.id === n.id))]));
      setPageState({ offset: offset + limit, limit });
    } catch (e) {
      console.warn('Failed to list conversations', e);
    } finally {
      setLoading(false);
    }
  }, [loading, pageState.offset, pageState.limit, debouncedQuery, includeArchived, activeClient]);

  // Reset list and reload on tenant change or search/filter updates
  useEffect(() => {
    if (!activeClient) {
      setConversations([]);
      setTotal(0);
      setPageState({ offset: 0, limit: 20 });
      return;
    }
    setConversations([]);
    setTotal(0);
    setPageState({ offset: 0, limit: pageState.limit });
    loadPage(true);
  }, [activeClient?.id, debouncedQuery, includeArchived, pageState.limit]);

  // Listen for global conversation update events to refresh first page (title changes, retries, archiving)
  // useEffect(() => {
  //   const handler = () => loadPage(true);
  //   window.addEventListener('conversation:updated', handler as any);
  //   return () => window.removeEventListener('conversation:updated', handler as any);
  // }, [loadPage]);

  const handleArchive = async (id: string) => {
    if (!activeClient) return;
    try {
      await deleteThread(activeClient.id, id);
      setConversations(prev => prev.filter(c => c.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
      window.dispatchEvent(new CustomEvent('conversation:updated', { detail: { id } }));
    } catch (e) {
      console.warn('Archive failed', e);
    }
  };

  if (!visible) {
    return (
      <Button onClick={onToggle} className="mb-2">
        Show History
      </Button>
    );
  }
  const grouped = useMemo(() => {
    // Deduplicate by id first
    const map = new Map<string, any>();
    for (const c of conversations) {
      if (!map.has(c.id)) map.set(c.id, c);
    }
    const unique = Array.from(map.values());
    const groups: Record<string, any[]> = {};
    unique.forEach((c: any) => {
      const key = dayjs(c.createdAt).format('YYYY-MM-DD');
      groups[key] = groups[key] || [];
      groups[key].push(c);
    });
    const orderedKeys = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));
    return orderedKeys.map((k) => ({ date: k, items: groups[k] }));
  }, [conversations]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Typography.Text className="!text-white">Conversations</Typography.Text>
          <div className="flex gap-1">
            <Button size="small" onClick={() => onSelectConversation('')}>New</Button>
            <Button size="small" onClick={onToggle}>Hide</Button>
          </div>
        </div>
        <Input
          placeholder="Search..."
          size="small"
          value={query}
            onChange={(e) => setQuery(e.target.value)}
          className="!bg-[#1F2937] !border-[#2d3748] !text-white"
          allowClear
        />
        <div className="flex items-center gap-1.5">
          <Switch size="small" checked={includeArchived} onChange={(v) => setIncludeArchived(v)} />
          <Typography.Text className="!text-[#9ca3af] !text-[11px]">Include Archived</Typography.Text>
        </div>
      </div>
      {grouped.map((g) => (
        <div key={g.date} className="bg-[#1F2937] border border-[#2d3748] rounded-lg p-2">
          <div className="flex items-center justify-between mb-1.5">
            <Typography.Text className="!text-[#f3f4f6] !text-xs !font-semibold">{dayjs(g.date).format('MMM D, YYYY')}</Typography.Text>
            <Badge count={g.items.length} className="[&_.ant-badge-count]:!bg-[#334155]" />
          </div>
          <List
            size="small"
            dataSource={g.items}
            split={false}
            renderItem={(item: any) => {
              const archived = !!item.archived;
              return (
                <List.Item
                  className="!cursor-pointer !px-2 !py-1.5 !rounded-md transition-colors hover:!bg-[#273447]"
                  data-archived={archived || undefined}
                  onClick={() => onSelectConversation(item.id)}>
                  <div className={`flex flex-col flex-1 ${archived ? 'opacity-55' : ''}`}>
                    <span className="text-white text-[13px]">
                      {item.title}
                      {archived && <span className="ml-1.5 text-[#fbbf24] text-[10px]">(Archived)</span>}
                    </span>
                    <span className="text-[10px] text-[#9ca3af]">
                      {dayjs(item.createdAt).format('HH:mm')}
                    </span>
                  </div>
                  {!archived && (
                    <Tooltip title="Archive conversation">
                      <Popconfirm title="Archive this conversation?" okText="Yes" cancelText="No" onConfirm={(e) => { e?.stopPropagation(); handleArchive(item.id); }}>
                        <Button size="small" type="text" className="!text-[#f87171]" onClick={(e) => e.stopPropagation()}>
                          ✕
                        </Button>
                      </Popconfirm>
                    </Tooltip>
                  )}
                </List.Item>
              );
            }}
          />
        </div>
      ))}
      {conversations.length < total && (
        <Button disabled={loading} loading={loading} onClick={() => loadPage(false)}>
          Load More ({conversations.length}/{total})
        </Button>
      )}
    </div>
  );
};
export default ConversationHistoryPanel;
